<!--
    What is waiting for the person signed in, in one place at the top of every screen.

    # Why it is here and not in the sidebar

    The sidebar already has a bell, and it is a different thing: "Opomniki" are dated
    notes somebody wrote for themselves about a booking. This one is not written by
    anybody — it is what the house is waiting on this account to do, and what the house
    has answered back. Merging the two would mean one list where half the rows can be
    ticked off and half cannot, and a count nobody could interpret.

    # What it is not

    It is not a feed. Everything in it is either work that stays until it is done (a
    discount waiting for this reader's decision) or an answer that is read once and gone
    (the decision on a discount this reader asked for). Nothing accumulates, so the
    number on it is always a number of things somebody can act on — which is the only
    reason a badge is worth drawing.

    The kinds live in `libs/notifications.ts`; this component knows about none of them.
    That includes the one press a row may carry: `action` is a label, an icon and a
    function, so this file draws a button without knowing what pressing it does — the
    same indirection the rest of the row has, for the same reason.
-->
<template>
    <UPopover v-if="available" v-model:open="open" :content="{ align: 'end' }">
        <UChip
            :text="items.length"
            :show="items.length > 0"
            color="warning"
            size="lg"
            inset
        >
            <UButton
                color="neutral"
                variant="ghost"
                icon="i-lucide-bell"
                size="lg"
                :aria-label="bellLabel"
                :title="bellLabel"
            />
        </UChip>

        <template #content>
            <div class="flex w-80 max-w-[90vw] flex-col gap-3 p-3 sm:w-96">
                <div class="flex items-baseline justify-between gap-2">
                    <span class="text-sm font-semibold text-highlighted">
                        {{ $t('notification.title') }}
                    </span>

                    <span v-if="items.length" class="text-xs tabular-nums text-muted">
                        {{ items.length }}
                    </span>
                </div>

                <p v-if="!items.length" class="text-sm text-muted">{{ $t('notification.empty') }}</p>

                <!-- Bounded and scrolled: a popover that grows past the window is one
                     whose last row cannot be reached. -->
                <div v-else class="flex max-h-96 flex-col gap-2 overflow-y-auto">
                    <RouterLink
                        v-for="item in items"
                        :key="item.id"
                        :to="item.to"
                        class="flex gap-3 rounded-lg border border-default p-2 hover:bg-elevated"
                        @click="open = false"
                    >
                        <UIcon :name="item.icon" :class="`mt-0.5 size-4 shrink-0 ${iconClass(item.color)}`" />

                        <div class="flex min-w-0 flex-col gap-0.5">
                            <span class="text-sm font-medium text-highlighted">{{ item.title }}</span>
                            <span class="text-sm text-toned">{{ item.detail }}</span>
                            <span class="text-xs text-dimmed">{{ item.meta }}</span>

                            <!-- The one press that finishes this row, inside the link
                                 rather than beside it so the row stays one target.
                                 `.stop.prevent` is load-bearing: without it the press
                                 would also follow the RouterLink, and the popover the
                                 answer lands in would already be gone. -->
                            <UButton
                                v-if="item.action"
                                class="mt-1 self-start"
                                color="neutral"
                                variant="subtle"
                                size="xs"
                                :icon="item.action.icon"
                                :label="item.action.label"
                                :loading="acting === item.id"
                                :disabled="acting !== ''"
                                @click.stop.prevent="run(item)"
                            />
                        </div>
                    </RouterLink>
                </div>
            </div>
        </template>
    </UPopover>
</template>

<script lang="ts" setup>
    import { computed, onMounted, ref, watch } from 'vue'
    import { useI18n } from 'vue-i18n'
    import { RouterLink, useRoute } from 'vue-router'

    import { useNotifications } from '@/libs/notifications'
    import { PrintError } from '@/libs/toast'
    import type { TagItem } from '@/types/Common'
    import type { INotification } from '@/types/Notification'

    const { t }  = useI18n()
    const route  = useRoute()

    const { items, available, reload, markSeen } = useNotifications()

    const open = ref(false)

    /**
     * The row currently being acted on, by id — so the pressed button spins and the
     * others are held, rather than a supervisor pressing four rows in a second and
     * four rebuilds of the list racing each other.
     */
    const acting = ref('')

    /**
     * Do a row's one press, and rebuild the list if it took.
     *
     * The row is not removed here. What is in the bell is what the service answers, and
     * a list that hid a row it had not confirmed gone would be a bell that lies for a
     * minute — on the exact kind of entry whose whole value is that it is true.
     */
    const run = async (item: INotification): Promise<void> => {
        if (!item.action || acting.value) return

        acting.value = item.id

        try {
            const took = await item.action.run()

            if (took) {
                await reload(true)

                return
            }

            // A press is not a background read, so the quiet rule above does not cover
            // it: somebody pressed and nothing visibly happened. The same toast the rota
            // screen shows for the same refusal, so the two places that offer this act
            // fail the same way.
            PrintError(t('notification.action_failed'))
        } finally {
            acting.value = ''
        }
    }

    const bellLabel = computed((): string => items.value.length
        ? t('notification.waiting', { count: items.value.length })
        : t('notification.title'))

    /**
     * The colour of a row's icon, as whole class names.
     *
     * A map rather than `text-${color}`: Tailwind reads the source text and generates
     * nothing it cannot see written down, so an assembled class name is a colour that
     * works on a developer's machine and is missing from the built stylesheet.
     */
    const iconClass = (color: TagItem['color']): string => ({
        primary:   'text-primary',
        secondary: 'text-secondary',
        success:   'text-success',
        info:      'text-info',
        warning:   'text-warning',
        error:     'text-error',
        neutral:   'text-muted',
    }[color] ?? 'text-muted')

    /**
     * Opening it is what "I have looked" means, and closing it is when the list is
     * rebuilt — so an answer that has been read is gone the moment the popover shuts,
     * rather than a minute later when the clock next comes round.
     */
    watch(open, opened => {
        if (opened) {
            markSeen()

            return
        }

        void reload(true)
    })

    // Every screen change, because a receptionist walking between screens is the
    // cheapest moment to find out that something is waiting — and because a desk that
    // never navigates is covered by the clock inside the composable.
    watch(() => route.fullPath, () => void reload())

    onMounted(() => void reload(true))
</script>
